import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { Mascot } from "../components/Mascot";
import { PublicSiteHeader, PublicSiteFooter } from "../components/PublicSiteChrome";
import {
  Calendar, Users, Stethoscope, ClipboardList, FileText, Receipt,
  BarChart3, Sparkles, ShieldCheck, ArrowRight, Check, Star, MessageCircle, Globe
} from "lucide-react";

const fadeIn = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.15 }
  }
};

export default function Landing() {
  const nav = useNavigate();
  const [plans, setPlans] = useState([]);
  
  useEffect(() => {
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/plans/public`)
      .then((r) => setPlans(r.data || []))
      .catch(() => setPlans([]));
  }, []);

  return (
    <div className="min-h-screen bg-slate-50 font-sans text-slate-800">
      <PublicSiteHeader />

      {/* Hero */}
      <section className="relative pt-32 pb-20 px-6 overflow-hidden flex items-center min-h-[90vh]">
        {/* Animated Background blobs */}
        <div className="absolute top-10 left-10 w-[500px] h-[500px] bg-blue-400/20 rounded-full mix-blend-multiply filter blur-[100px] animate-blob"></div>
        <div className="absolute top-10 right-10 w-[500px] h-[500px] bg-teal-400/20 rounded-full mix-blend-multiply filter blur-[100px] animate-blob animation-delay-2000"></div>
        <div className="absolute -bottom-32 left-1/2 w-[500px] h-[500px] bg-emerald-400/20 rounded-full mix-blend-multiply filter blur-[100px] animate-blob animation-delay-4000"></div>

        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 items-center relative z-10">
          <motion.div 
            variants={staggerContainer}
            initial="hidden"
            animate="visible"
            className="lg:col-span-7"
          >
            <motion.span variants={fadeIn} className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-mg-medical bg-white/60 backdrop-blur-sm border border-white/40 shadow-sm px-4 py-2 rounded-full mb-6">
              <Sparkles className="w-3.5 h-3.5 text-mg-gold" /> Advanced Healthcare Platform
            </motion.span>
            <motion.h1 variants={fadeIn} className="text-4xl sm:text-5xl lg:text-7xl font-extrabold text-mg-navy tracking-tight leading-[1.15]">
              Run your clinic, <br className="hidden sm:block" />
              <span className="text-transparent bg-clip-text mg-gradient-primary leading-tight pb-1 sm:pb-2 inline-block">end to end</span>, <br className="hidden sm:block" />
              with a little magic.
            </motion.h1>
            <motion.p variants={fadeIn} className="mt-6 text-lg sm:text-xl text-slate-600 max-w-2xl leading-relaxed">
              MediGenie helps clinics, doctors and hospitals manage online bookings,
              schedules, encounters, prescriptions, payments and follow-ups — guided
              by a friendly, role-aware healthcare assistant.
            </motion.p>
            <motion.div variants={fadeIn} className="mt-8 sm:mt-10 flex flex-col sm:flex-row gap-4">
              <Link to="/register" className="w-full sm:w-auto text-center px-8 py-4 rounded-xl text-white font-bold text-lg shadow-xl hover:shadow-2xl transition-all hover:-translate-y-1 mg-gradient-primary" data-testid="hero-start-trial">
                Start Free Trial
              </Link>
              <a href="#features" className="w-full sm:w-auto text-center px-8 py-4 rounded-xl font-bold text-lg bg-white/80 backdrop-blur-md shadow-lg border border-white/40 hover:bg-white hover:-translate-y-1 transition-all text-mg-medical" data-testid="hero-book-demo">
                Explore Features
              </a>
            </motion.div>
            <motion.div variants={fadeIn} className="mt-6 sm:mt-8 flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-6 text-sm font-semibold text-slate-500">
              <div className="flex items-center gap-2"><Check className="w-4 h-4 text-mg-health" /> 7-day Starter trial</div>
              <div className="flex items-center gap-2"><Check className="w-4 h-4 text-mg-health" /> No card required</div>
              <div className="flex items-center gap-2"><Check className="w-4 h-4 text-mg-health" /> Cancel anytime</div>
            </motion.div>
          </motion.div>
          
          <motion.div 
            initial={{ opacity: 0, scale: 0.9, rotate: -2 }}
            animate={{ opacity: 1, scale: 1, rotate: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="lg:col-span-5 relative mt-8 lg:mt-0"
          >
            <div className="relative mg-gradient-primary rounded-[2.5rem] sm:rounded-[3rem] p-5 sm:p-8 shadow-2xl">
              <div className="absolute -top-6 -right-6 sm:-top-8 sm:-right-8 bg-white w-20 h-20 sm:w-24 sm:h-24 rounded-full flex items-center justify-center mg-float shadow-2xl border-4 border-slate-50">
              </div>
              <div className="bg-white/95 backdrop-blur-xl rounded-[2rem] sm:rounded-[2.5rem] p-6 sm:p-8 shadow-inner border border-white/50">
                <div className="flex items-center gap-3 sm:gap-4 mb-6 pb-6 border-b border-slate-100">
                  <Mascot size={112} className="mg-float drop-shadow-md hidden sm:block" />
                  <Mascot size={96} className="mg-float drop-shadow-md sm:hidden" />
                  <div>
                    <div className="font-extrabold text-lg sm:text-xl text-mg-navy">MediGenie</div>
                    <div className="text-xs sm:text-sm font-semibold text-mg-gold">Your healthcare buddy ✨</div>
                  </div>
                </div>
                <div className="space-y-4 text-xs sm:text-sm font-medium">
                  <div className="bg-slate-100/80 rounded-2xl rounded-bl-sm px-3 py-2.5 sm:px-4 sm:py-3 shadow-sm border border-slate-200/50">
                    Hi Dr. Anjali! How are you today?
                  </div>
                  <div className="mg-gradient-primary text-white rounded-2xl rounded-br-sm px-3 py-2.5 sm:px-4 sm:py-3 ml-6 sm:ml-8 shadow-md">
                    Show me my appointments
                  </div>
                  <div className="bg-slate-100/80 rounded-2xl rounded-bl-sm px-3 py-2.5 sm:px-4 sm:py-3 shadow-sm border border-slate-200/50">
                    You have <strong className="text-mg-medical">5 appointments</strong> today and <strong className="text-mg-health">2 follow-ups</strong> due. Would you like to see your schedule?
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="py-32 px-6 relative bg-white">
        <div className="max-w-7xl mx-auto relative z-10">
          <motion.div 
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={fadeIn}
            className="text-center mb-20"
          >
            <span className="text-sm font-bold uppercase tracking-wider text-mg-medical bg-blue-50 px-4 py-2 rounded-full">Everything you need</span>
            <h2 className="text-4xl sm:text-5xl font-extrabold text-mg-navy tracking-tight mt-6">
              One intelligent platform for every clinic
            </h2>
          </motion.div>
          <motion.div 
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-50px" }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
          >
            {[
              { icon: Calendar, title: "Online appointment booking", desc: "Beautiful clinic booking pages that fill your calendar automatically.", color: "text-mg-medical bg-blue-50" },
              { icon: Stethoscope, title: "Doctor schedules", desc: "Set availability, services, breaks and consultation fees per doctor.", color: "text-mg-genie bg-cyan-50" },
              { icon: Users, title: "Patient management", desc: "Records, history, allergies, documents and a clean timeline.", color: "text-mg-health bg-green-50" },
              { icon: ClipboardList, title: "Encounter notes", desc: "Vitals, observations, diagnosis and clinical notes in one workflow.", color: "text-mg-gold bg-amber-50" },
              { icon: FileText, title: "Prescriptions & follow-ups", desc: "Generate prescriptions and schedule follow-ups linked to treatment chains.", color: "text-mg-medical bg-blue-50" },
              { icon: Receipt, title: "Billing & invoices", desc: "Invoices, taxes, payments, partial collection — all from the front desk.", color: "text-mg-genie bg-cyan-50" },
              { icon: BarChart3, title: "Advanced reporting", desc: "Doctor-wise, service-wise and clinic-wide insights, instantly.", color: "text-mg-health bg-green-50" },
              { icon: Sparkles, title: "Role-aware MediGenie", desc: "An assistant that answers based on each user's role and permissions.", color: "text-mg-gold bg-amber-50" },
              { icon: Globe, title: "Dedicated Clinic Website", desc: "List your services, showcase doctors, and get appointments directly online.", color: "text-mg-medical bg-blue-50" },
            ].map((f, i) => (
              <motion.div variants={fadeIn} key={i} className="bg-white rounded-3xl p-8 shadow-xl shadow-slate-200/40 hover:shadow-2xl border border-slate-100 hover:-translate-y-2 transition-all group" data-testid={`feature-card-${i}`}>
                <div className={`w-14 h-14 rounded-2xl flex items-center justify-center mb-6 ${f.color} group-hover:scale-110 transition-transform duration-300`}>
                  <f.icon className="w-7 h-7" />
                </div>
                <h3 className="font-extrabold text-mg-navy mt-4 text-xl">{f.title}</h3>
                <p className="text-slate-500 mt-3 leading-relaxed text-base font-medium">{f.desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* How it works */}
      <section id="how" className="py-32 px-6 bg-slate-50 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-[800px] h-[800px] bg-white rounded-full mix-blend-multiply filter blur-[120px] opacity-60"></div>
        <div className="max-w-6xl mx-auto relative z-10">
          <motion.h2 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-4xl sm:text-5xl font-extrabold text-mg-navy text-center tracking-tight mb-20"
          >
            How it works
          </motion.h2>
          <motion.div 
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid grid-cols-1 md:grid-cols-3 gap-8"
          >
            {[
              ["1", "Create your clinic", "Sign up, add doctors and services in minutes."],
              ["2", "Share your booking page", "Patients book online instantly — no calls needed."],
              ["3", "Manage everything", "Encounters, prescriptions, billing, follow-ups."],
            ].map(([n, t, d]) => (
              <motion.div variants={fadeIn} key={n} className="relative mt-6 md:mt-0">
                <div className="bg-white rounded-3xl p-8 pt-14 shadow-xl shadow-slate-200/50 border border-slate-100 hover:-translate-y-2 transition-all h-full">
                  <div className="absolute -top-8 left-8 mg-gradient-primary w-16 h-16 rounded-2xl text-white text-2xl font-extrabold flex items-center justify-center shadow-lg transform rotate-3 hover:rotate-6 transition-transform">{n}</div>
                  <h3 className="font-extrabold text-mg-navy text-2xl mb-3">{t}</h3>
                  <p className="text-lg text-slate-500 font-medium leading-relaxed">{d}</p>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="py-32 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <motion.div 
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeIn}
            className="text-center mb-20"
          >
            <span className="text-sm font-bold uppercase tracking-wider text-mg-medical bg-blue-50 px-4 py-2 rounded-full">Simple pricing</span>
            <h2 className="text-4xl sm:text-5xl font-extrabold text-mg-navy tracking-tight mt-6">Plans that grow with you</h2>
          </motion.div>
          <motion.div 
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className={`grid grid-cols-1 md:grid-cols-2 ${plans.length >= 4 ? "lg:grid-cols-4" : "lg:grid-cols-" + Math.max(plans.length, 1)} gap-8`}
          >
            {plans.length === 0 && (
              <div className="col-span-full text-center text-slate-400 italic py-12">Pricing loading…</div>
            )}
            {plans.map((p) => {
              const price = p.monthly_price === 0
                ? "Free"
                : `${p.currency === "INR" ? "₹" : p.currency + " "}${Number(p.monthly_price).toLocaleString()}`;
              const bullets = [
                p.branch_limit == null
                  ? "Unlimited active branches"
                  : `${p.branch_limit} active branch${Number(p.branch_limit) === 1 ? "" : "es"}`,
                ...(p.features_list || []).slice(0, 5).map((k) => k.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())),
              ];
              if (p.premium_ai) bullets.push("Premium MediGenie AI");
              else if (p.chatbot_basic) bullets.push("Rule-based MediGenie");
              return (
                <motion.div variants={fadeIn} key={p.plan_id} className={`bg-white rounded-3xl p-8 flex flex-col shadow-xl shadow-slate-200/50 hover:shadow-2xl hover:-translate-y-2 transition-all ${p.is_recommended ? "ring-4 ring-mg-medical relative transform md:-translate-y-4 md:hover:-translate-y-6" : "border border-slate-100"}`} data-testid={`pricing-${(p.slug || p.name).toLowerCase()}`}>
                  {p.is_recommended && <div className="absolute -top-4 left-1/2 -translate-x-1/2 mg-gradient-magical text-mg-navy text-xs uppercase tracking-widest font-extrabold px-4 py-1.5 rounded-full shadow-lg">Recommended</div>}
                  <h3 className="font-extrabold text-mg-navy text-2xl">{p.name}</h3>
                  {p.short_description && <p className="text-sm font-medium text-slate-500 mt-2 leading-relaxed h-10">{p.short_description}</p>}
                  <div className="mt-6 mb-6">
                     <span className="text-5xl font-extrabold text-mg-navy tracking-tight">{price}</span>
                     {p.monthly_price > 0 && <span className="text-lg font-bold text-slate-400 ml-1">/mo</span>}
                  </div>
                  <div className="flex flex-wrap gap-2 mb-8">
                    {p.trial_enabled && p.trial_days > 0 && (
                      <span className="text-xs font-bold uppercase tracking-wider bg-green-50 text-mg-health border border-green-100 rounded-lg px-3 py-1">{p.trial_days}-day free trial</span>
                    )}
                    {p.premium_ai && (
                      <span className="text-xs font-bold uppercase tracking-wider mg-gradient-magical text-mg-navy shadow-sm rounded-lg px-3 py-1">Premium AI</span>
                    )}
                  </div>
                  <ul className="space-y-4 flex-1">
                    {bullets.map((b, i) => (
                      <li key={b + i} className="flex items-start gap-3 text-base font-medium text-slate-600">
                        <div className="w-6 h-6 rounded-full bg-green-50 flex items-center justify-center shrink-0 mt-0.5">
                           <Check className="w-4 h-4 text-mg-health" />
                        </div>
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>
                  <button onClick={() => nav("/register", { state: { plan_id: p.plan_id } })} className={`mt-10 py-4 rounded-xl text-lg font-bold w-full transition-all ${p.is_recommended ? "mg-gradient-primary text-white shadow-lg hover:shadow-xl" : "bg-slate-100 text-mg-navy hover:bg-slate-200"}`} data-testid={`pricing-cta-${(p.slug || p.name).toLowerCase()}`}>
                    Get started
                  </button>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 px-6 relative z-10">
        <motion.div 
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="max-w-5xl mx-auto mg-gradient-primary rounded-[3rem] px-8 py-20 text-center text-white relative overflow-hidden shadow-2xl"
        >
          <div className="absolute inset-0 opacity-20 mix-blend-overlay" style={{backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)', backgroundSize: '40px 40px'}}></div>
          <div className="relative z-10">
             <Mascot size={288} className="mx-auto mb-8 mg-float drop-shadow-xl" />
             <h2 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-tight">Healthcare with a little magic ✨</h2>
             <p className="mt-6 text-xl opacity-90 max-w-2xl mx-auto font-medium leading-relaxed">
               First click to better health — MediGenie is with you, at every step.
             </p>
             <button onClick={() => nav("/register")} className="mt-10 bg-white text-mg-medical font-extrabold text-lg px-10 py-5 rounded-2xl hover:scale-105 shadow-xl hover:shadow-2xl transition-all inline-flex items-center gap-3" data-testid="cta-start">
               Start your free trial <ArrowRight className="w-5 h-5" />
             </button>
          </div>
        </motion.div>
      </section>

      {/* FAQ */}
      <section id="faq" className="py-32 px-6 bg-slate-50">
        <div className="max-w-3xl mx-auto">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-10 sm:mb-16"
          >
             <h2 className="text-3xl sm:text-4xl font-extrabold text-mg-navy tracking-tight">Frequently asked questions</h2>
          </motion.div>
          <motion.div 
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="space-y-4"
          >
            {[
              ["Do I need a credit card to try MediGenie?", "No. The Starter plan includes a 7-day free trial with no card on file."],
              ["Do I need a premium package to get a clinic website?", "No. Even without a premium package, you have access to your own website where you can list your services and doctors, and get appointments directly."],
              ["Does MediGenie work for multi-doctor clinics?", "Absolutely — multi-doctor schedules, services, and doctor-wise revenue are built in."],
              ["Who can use MediGenie within my clinic?", "Clinic administrators, doctors, receptionists, billing teams, and other authorised staff can each receive role-based access suited to their work."],
              ["Can I manage more than one clinic location?", "Yes. MediGenie supports clinic branches so your team can organise locations while working from one connected platform."],
              ["How does MediGenie help with patient follow-ups?", "Follow-ups can be scheduled from the clinical workflow and linked to the patient's treatment journey, helping your team maintain continuity of care."],
              ["Is my patient data secure?", "Yes. Strict data separation, detailed audit logs, and granular role-based access control protect your clinic's data."],
              ["Can I take payments online?", "Razorpay payment workflows are supported when your clinic configures valid provider credentials. You can also record approved offline payment methods."],
            ].map(([q, a], i) => (
              <motion.details variants={fadeIn} key={i} className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100 cursor-pointer group" data-testid={`faq-${i}`}>
                <summary className="font-bold text-lg text-mg-navy list-none flex justify-between items-center outline-none">
                   {q}
                   <span className="text-mg-medical group-open:rotate-45 transition-transform duration-300 text-2xl">+</span>
                </summary>
                <p className="mt-4 text-base font-medium text-slate-500 leading-relaxed pr-8">{a}</p>
              </motion.details>
            ))}
          </motion.div>
        </div>
      </section>

      <PublicSiteFooter />
    </div>
  );
}
